"use client";

import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { ShopProfileHeader } from "./ShopProfileHeader";
import { ShopProductFilterBar } from "./ShopProductFilterBar";
import { ShopProductGrid } from "./ShopProductGrid";
import { useShopProducts } from "./useShopProducts";
import type {
  PublicCommunityShop,
  PublicCommunityProduct,
  PublicCommunityShopProfileResponse,
} from "@/lib/community/types";

interface ShopProfilePageProps {
  shop: PublicCommunityShop;
  totalProducts: number;
  totalPartners?: number;
  featuredCollections?: PublicCommunityShopProfileResponse["featured_collections"];
  initialProducts: PublicCommunityProduct[];
  returnTo?: string;
}

export function ShopProfilePage({
  shop,
  totalProducts,
  totalPartners,
  featuredCollections,
  initialProducts,
  returnTo,
}: ShopProfilePageProps) {
  const {
    products,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    searchQuery,
    setParams,
    fetchNextPage,
    error,
    refetch,
    isFetchNextPageError,
  } = useShopProducts(shop.slug ?? "", {
    products: initialProducts,
    total: totalProducts,
  });

  return (
    <>
      <ShopProfileHeader
        shop={shop}
        totalProducts={totalProducts}
        totalPartners={totalPartners}
        featuredCollections={featuredCollections}
      />

      <Section id="products" background="white" size="sm" className="pt-4 md:pt-6 lg:pt-8">
        <Container size="xl">
          <ShopProductFilterBar
            searchQuery={searchQuery}
            onSearchChange={(q) => setParams({ q })}
          />

          <ShopProductGrid
            products={products}
            total={total}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            shopSlug={shop.slug}
            error={error}
            onRetry={isFetchNextPageError ? fetchNextPage : refetch}
            onClearSearch={() => setParams({ q: "" })}
            returnTo={returnTo}
          />
        </Container>
      </Section>
    </>
  );
}
